import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const inp = 'w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] bg-white focus:outline-none focus:border-[#002FA7]';

const Resend = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const send = async () => {
    try { await axios.post(`${API}/api/public/arena-activation/resend`, { email }); setSent(true); }
    catch (e) { toast.error(e.response?.data?.detail || 'Please try again in a few minutes'); }
  };
  return sent ? <p className="text-sm text-emerald-700" data-testid="activation-resend-sent">If this email has a registration, a new link is on its way.</p> : (
    <div className="flex gap-2">
      <input className={inp} type="email" placeholder="Your registration email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="activation-resend-email" />
      <button onClick={send} disabled={!email} className="px-4 bg-[#0A0A0A] text-white rounded text-sm font-semibold disabled:opacity-40" data-testid="activation-resend-btn">Send</button>
    </div>
  );
};

// Set-password page reached from the emailed activation link; creates the Arena account and links all registrations.
export default function ArenaActivatePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError('This activation link is incomplete.'); return; }
    axios.get(`${API}/api/public/arena-activation/${encodeURIComponent(token)}`).then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'This activation link is invalid'));
  }, [token]);

  const submit = async () => {
    if (pw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pw !== pw2) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/public/arena-activation/complete`, { token, password: pw });
      localStorage.setItem('codearena_token', data.access_token);
      toast.success('Account activated — your registrations are linked');
      navigate('/codearena/dashboard');
    } catch (e) {
      const d = e.response?.data?.detail || 'Activation failed';
      if (e.response?.status === 409) { toast.error(d); navigate('/codearena/auth'); } else { toast.error(d); setError(d); }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4 py-12" data-testid="arena-activate-page">
      <div className="w-full max-w-md bg-white border border-[#E4E4E7] rounded-xl p-6 sm:p-8 space-y-5">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A1A1AA]">EvalentX Arena</p>
          <h1 className="text-2xl font-bold text-[#0A0A0A] mt-1">Activate your account</h1>
        </div>
        {error ? (
          <div className="space-y-4" data-testid="activation-error">
            <p className="text-sm text-red-600">{error}</p>
            <p className="text-sm text-[#52525B]">Request a new link (valid 7 days):</p>
            <Resend />
            <Link to="/codearena/auth" className="block text-sm font-semibold text-[#002FA7]">Already have an account? Log in</Link>
          </div>
        ) : !info ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#A1A1AA]" /></div> : (
          <div className="space-y-4">
            <p className="text-sm text-[#52525B]">Hi <b className="text-[#0A0A0A]">{info.name || 'there'}</b>{info.event_title && <> — you're registered for <b className="text-[#0A0A0A]">{info.event_title}</b></>}. Set a password for <b className="text-[#0A0A0A]" data-testid="activation-email">{info.email}</b>.</p>
            <ul className="text-xs text-[#52525B] space-y-1">
              {['Your registration & ticket', 'Your team and teammates', 'Project submissions'].map((t) => <li key={t} className="flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-600" /> {t} link automatically</li>)}
            </ul>
            <input className={inp} type="password" placeholder="New password (min 8 characters)" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="activation-password" />
            <input className={inp} type="password" placeholder="Confirm password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" data-testid="activation-password-confirm" />
            <button onClick={submit} disabled={busy} className="w-full py-3 bg-[#002FA7] hover:bg-[#00227A] text-white rounded font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="activation-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />} Activate account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
